import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { theme } from "../../lib/theme";
import type { TxInsightsResponse } from "../../lib/api";
import { formatFJ, formatFJCompact } from "../../lib/format";
import { noAnimation } from "../../lib/chart";
import { abbreviateHex } from "../TxTable";
import { setLabel } from "../../lib/view-insights";
import { ChartCard, ChartTitle } from "../ui";

/**
 * What a saved view's transactions actually paid, tx by tx over block height:
 * a band that drifts with base fees reads as one population, while a split
 * into two bands usually means two wallets or fee strategies hiding under the
 * same filters. Log scale, since a fee range routinely spans orders of
 * magnitude.
 */

const Subtitle = styled.p`
  margin: 0 0 ${theme.spacing.md};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
`;

const Empty = styled.p`
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.sm};
  margin: 0;
`;

const TooltipBox = styled.div`
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.sm};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  font-size: 11px;
  font-family: monospace;
  color: ${theme.colors.text};
  line-height: 1.5;
`;

interface Point {
  block: number;
  fee: number;
  txHash: string;
  clusterId: number | null;
}

/** Floor for the plot; beside the privacy card it grows to match that card's height. */
const MIN_CHART_HEIGHT = 300;

const FillCard = styled(ChartCard)`
  display: flex;
  flex-direction: column;
`;

/**
 * Takes whatever height the card has left. ResponsiveContainer needs a parent
 * with a resolved size, so the chart sits in an absolutely positioned layer
 * over this flex slot instead of sizing the slot itself.
 */
const ChartArea = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: ${MIN_CHART_HEIGHT}px;
`;

const ChartLayer = styled.div`
  position: absolute;
  inset: 0;
`;

export interface FeeSpreadCardProps {
  networkId: string;
  insights: TxInsightsResponse | undefined;
  /** Median fee of the whole matching set (from /txs/stats), drawn as a guide. */
  medianFee?: string | null;
  /** Selected privacy sets, drawn over the rest in their colours. */
  selected?: number[];
  colorOf?: (clusterId: number) => string | undefined;
}

export function FeeSpreadCard({
  networkId,
  insights,
  medianFee,
  selected = [],
  colorOf,
}: FeeSpreadCardProps) {
  const navigate = useNavigate();

  // A log axis can't place 0, so zero-fee txs are counted in the subtitle instead of plotted.
  const { points, zeroFee } = useMemo(() => {
    const points: Point[] = [];
    let zeroFee = 0;
    for (const p of insights?.feePoints ?? []) {
      const fee = Number(p.actualFee);
      if (fee > 0)
        points.push({ block: p.blockNumber, fee, txHash: p.txHash, clusterId: p.clusterId });
      else zeroFee++;
    }
    return { points, zeroFee };
  }, [insights?.feePoints]);

  // With sets selected, the rest of the view recedes to grey and each set is
  // its own series on top, so its txs can't hide under the crowd.
  const layers = useMemo(() => {
    if (selected.length === 0) return null;
    const chosen = new Set(selected);
    return {
      rest: points.filter((p) => p.clusterId === null || !chosen.has(p.clusterId)),
      sets: selected.map((id) => ({ id, points: points.filter((p) => p.clusterId === id) })),
    };
  }, [points, selected]);

  const openTx = (d: { payload?: Point }) => {
    if (d.payload) navigate(`/${networkId}/tx/${d.payload.txHash}`);
  };

  const median = medianFee != null ? Number(medianFee) : null;
  const sampled = insights ? insights.feePointsTotal > insights.feePoints.length : false;

  return (
    <FillCard>
      <ChartTitle>Fee spread</ChartTitle>
      <Subtitle>
        {!insights
          ? "Loading…"
          : insights.feePointsTotal === 0
            ? "None of the matching transactions have been mined yet."
            : `Actual fee per mined tx by block${
                sampled
                  ? `, the ${insights.feePoints.length.toLocaleString()} most recent of ${insights.feePointsTotal.toLocaleString()}`
                  : ` (${insights.feePointsTotal.toLocaleString()} txs)`
              }. Dashed line: median over all matches.${zeroFee > 0 ? ` ${zeroFee.toLocaleString()} zero-fee txs not shown.` : ""} Click a point to open the tx.`}
      </Subtitle>

      {insights && points.length > 0 ? (
        <ChartArea>
          <ChartLayer>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} opacity={0.3} />
                <XAxis
                  dataKey="block"
                  type="number"
                  name="Block"
                  domain={["dataMin", "dataMax"]}
                  stroke={theme.colors.textMuted}
                  fontSize={10}
                  tickFormatter={(v) => `#${v}`}
                />
                <YAxis
                  dataKey="fee"
                  type="number"
                  name="Fee"
                  scale="log"
                  domain={["dataMin", "dataMax"]}
                  stroke={theme.colors.textMuted}
                  fontSize={10}
                  tickFormatter={(v) => formatFJCompact(v)}
                  label={{
                    value: "FJ",
                    angle: -90,
                    position: "insideLeft",
                    fill: theme.colors.textMuted,
                    fontSize: 10,
                  }}
                />
                {/* Small fixed markers: thousands of txs overlap, and big dots turn the bands into one blob. */}
                <ZAxis range={[18, 18]} />
                <Tooltip
                  cursor={{ stroke: theme.colors.border, strokeDasharray: "3 3" }}
                  content={({ active, payload }) => {
                    const p = active ? (payload?.[0]?.payload as Point | undefined) : undefined;
                    if (!p) return null;
                    return (
                      <TooltipBox>
                        <div>Block #{p.block}</div>
                        <div>{formatFJ(p.fee)}</div>
                        <div style={{ color: theme.colors.textMuted }}>
                          {abbreviateHex(p.txHash)}
                        </div>
                        {p.clusterId !== null && (
                          <div style={{ color: theme.colors.textMuted }}>
                            {setLabel(p.clusterId)}
                          </div>
                        )}
                      </TooltipBox>
                    );
                  }}
                />
                {median != null && median > 0 && (
                  <ReferenceLine
                    y={median}
                    stroke={theme.colors.accent}
                    strokeDasharray="4 4"
                    strokeWidth={1.5}
                  />
                )}
                <Scatter
                  data={layers ? layers.rest : points}
                  fill={layers ? theme.colors.textMuted : theme.colors.primary}
                  fillOpacity={layers ? 0.25 : 0.55}
                  shape="circle"
                  cursor="pointer"
                  onClick={openTx}
                  {...noAnimation}
                />
                {layers?.sets.map((set) => (
                  <Scatter
                    key={set.id}
                    data={set.points}
                    fill={colorOf?.(set.id)}
                    fillOpacity={0.85}
                    shape="circle"
                    cursor="pointer"
                    onClick={openTx}
                    {...noAnimation}
                  />
                ))}
              </ScatterChart>
            </ResponsiveContainer>
          </ChartLayer>
        </ChartArea>
      ) : (
        insights && insights.feePointsTotal > 0 && <Empty>Every sampled tx paid zero fees.</Empty>
      )}
    </FillCard>
  );
}
